import type {
    ContentRef,
    ContentRefFile,
    ContentRefReusableContent,
    ContentRefURL,
    IntegrationsBlocksSet,
} from '@gitbook/api-client';
import type { DocumentSchemaType } from '@gitbook/doc-core';
import type { ExpressionRuntime } from '@gitbook/expr';
import type { MaybePromise } from '@gitbook/util-ts';
import { isUrl } from '@gitbook/util-url';

export interface ParsingFileOptions {
    fileName?: string;
}

/**
 * Options for parsing content.
 */
export interface ParsingOptions {
    mode?: 'page' | 'document';

    /**
     * Schema type to use for parsing. Defaults to 'document'
     */
    documentSchemaType?: DocumentSchemaType;

    /**
     * Resolve a link to a content ref.  If it returns null, the link/mention is ignored.
     */
    resolveLink: (src: string) => MaybePromise<ContentRef | null>;

    /**
     * Resolve a reusable content to a content ref. If it returns null, the content is ignored.
     */
    resolveReusableContent?: (src: string) => MaybePromise<ContentRefReusableContent | null>;

    /**
     * Resolve a file source to an absolute url. If it returns null, the file is ignored.
     */
    resolveFile: (
        src: string,
        options?: ParsingFileOptions
    ) => MaybePromise<ContentRefFile | ContentRefURL | null>;

    /**
     * Adaptive content expression runtime that include a parser.
     * */
    expressionRuntime?: ExpressionRuntime;

    /**
     * Integration custom blocks to parse.
     */
    integrationBlocks?: IntegrationsBlocksSet;
}

export const defaultParsingOptions: ParsingOptions = {
    resolveLink: async (src) => {
        if (isUrl(src)) {
            return { kind: 'url', url: src };
        }
        return null;
    },
    resolveFile: async (src) => {
        if (isUrl(src)) {
            return { kind: 'url', url: src };
        }
        return null;
    },
};
